import { INotificationMessage } from "@fl/shared/src/types/messages";
import { ConfiguracoesTokensAttributes } from "@fl/shared/src/types/models/suporte_fl/tokens";

import { IMovimentacao } from "@fl/shared/src/types/models/superlogica/assinaturas";
import {
  IDataExcel,
  IDataPDF,
  IExcelColumn,
  IFormFieldReport,
} from "@fl/shared/src/types/reports";
import { ITableDataColumns } from "@fl/shared/src/types/tables";
import moment from "moment";
import numeral from "numeral";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import { STATUS_DESPESAS_ASSINATURAS } from "../../../components/select/defaultValuesSL";
import SelectComp from "../../../components/select/selectComp";
import { useNotificationContext } from "../../../context/notifications";
import ApiConfigTokens from "../../../services/suporte/auth/configs/tokens";
import ApiReportFinancial from "../../../services/suporte/auth/reports/financial";
import { getSelectOptions } from "../../../utils/selectFuncs";
import DefaultBasePage from "../../default/base";
import DefaultReportPage from "../../default/reports";
import StringsFuncs from "../../../utils/stringsFuncs";

// --------- utils: datas default = mês anterior ---------
const prevMonthStart = moment()
  .subtract(1, "month")
  .startOf("month")
  .format("YYYY-MM-DD");

const prevMonthEnd = moment()
  .subtract(1, "month")
  .endOf("month")
  .format("YYYY-MM-DD");

/** Remove tags HTML e converte <br> em quebras de linha simples */
const stripHtmlHistorico = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();

// formatos possíveis vindos da API
const DATE_IN_FORMATS = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "YYYY-MM-DD",
  "DD/MM/YYYY HH:mm:ss",
  "MM/DD/YYYY HH:mm:ss",
];

// parse robusto -> "DD/MM/YYYY" ou "-" se vazio
const fmtDate = (s?: string) => {
  if (!s) return "-";
  const m = moment(s, DATE_IN_FORMATS, true);
  if (m.isValid()) return m.format("DD/MM/YYYY");
  const m2 = moment(s);
  return m2.isValid() ? m2.format("DD/MM/YYYY") : "-";
};

const money = (n: unknown) => numeral(Number(n) || 0).format("#,##0.00");

const clean = (t?: string) =>
  stripHtmlHistorico(t || "")
    .replace(/\s+/g, " ")
    .replace(/["“”]+/g, "")
    .trim();

const natLabel = (n?: IMovimentacao["natureza"]) =>
  n === "credito" ? "Crédito" : n === "debito" ? "Débito" : n || "";

const orgLabel = (o?: IMovimentacao["tipo"]) =>
  o === "eventuais"
    ? "Receita Extra"
    : o === "despesas"
      ? "Despesa"
      : o === "receitas"
        ? "Receita"
        : o || "";

const RelatoriosMovimentacaoEmpresasPrivatePage = () => {
  const { addNotification } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [tokens, setTokens] = useState<ConfiguracoesTokensAttributes[]>([]);
  const [results, setResults] = useState<IMovimentacao[]>([]);

  useEffect(() => {
    const loadTokens = async () => {
      const result = await new ApiConfigTokens().list();
      if (result.success) {
        setTokens(result.data);
      }
    };

    loadTokens();
  }, []);

  // --------- exports (mínimo viável) ----------
  const getExportDataPDF = (): IDataPDF => {
    const movs: IMovimentacao[] = Array.isArray(results)
      ? results
      : ((results as IMovimentacao[]) ?? []);

    const header = [
      { text: "ID", alignment: "center", bold: true },
      { text: "Empresa", alignment: "center", bold: true },
      { text: "Data Venc.", alignment: "center", bold: true },
      { text: "Data Liq./Rec.", alignment: "center", bold: true },
      { text: "Natureza", alignment: "center", bold: true },
      { text: "Origem", alignment: "center", bold: true },
      { text: "Favorecido / Sacado", alignment: "center", bold: true },
      { text: "Conta", alignment: "center", bold: true },
      { text: "Banco", alignment: "center", bold: true },
      { text: "Agencia", alignment: "center", bold: true },
      { text: "Corrente", alignment: "center", bold: true },
      { text: "Forma Pagto.", alignment: "center", bold: true },
      { text: "Documento", alignment: "center", bold: true }, // << mantém
      { text: "Valor", alignment: "center", bold: true },
    ];

    const body = movs.map((r) => {
      const natureza =
        r.natureza === "credito"
          ? "Crédito"
          : r.natureza === "debito"
            ? "Débito"
            : r.natureza;

      const origem =
        r.tipo === "eventuais"
          ? "Receita Extra"
          : r.tipo === "despesas"
            ? "Despesa"
            : r.tipo === "receitas"
              ? "Receita"
              : r.tipo;

      const favorecido = (r.fornecedor || "").toString().toUpperCase().trim();
      const descricao = stripHtmlHistorico(r.historico || "");

      return [
        { text: r.id || "", alignment: "center" }, // << agora “Documento”
        { text: r.empresa || "", alignment: "center" },
        { text: fmtDate(r.data_vencimento), alignment: "center" },
        { text: fmtDate(r.data_liquidacao), alignment: "center" },
        { text: natureza, alignment: "center" },
        { text: origem, alignment: "center" },
        { text: favorecido, alignment: "center" },
        { text: r.banco || "", alignment: "center" },
        { text: r.banco_nome || "", alignment: "center" },
        { text: r.banco_agencia || "", alignment: "center" },
        { text: r.banco_conta || "", alignment: "center" },
        { text: r.forma || "", alignment: "center" },
        { text: descricao, alignment: "left" }, // << descrição depois
        { text: money(r.valor), alignment: "right" },
      ];
    });

    const totalGeral = movs.reduce((acc, r) => acc + (Number(r.valor) || 0), 0);
    if (movs.length) {
      body.push([
        { text: "TOTAL", colSpan: 10, alignment: "right", bold: true },
        ...Array(9).fill({ text: "" }),
        { text: money(totalGeral), alignment: "right", bold: true },
      ]);
    }

    return {
      headerTitle: "Fluxo Financeiro (Movimentação Financeira)",
      pageOrientation: "landscape",
      content: {
        style: "table",
        table: { headerRows: 1, body: [header, ...body] },
        layout: "lightHorizontalLines",
      },
      styles: { table: { fontSize: 8 } },
    };
  };

  const getExportDataExcel = (): IDataExcel => {
    const movs: IMovimentacao[] = Array.isArray(results)
      ? results
      : ((results as IMovimentacao[]) ?? []);

    const columns: IExcelColumn[] = [
      { header: "ID", width: 22 }, // << mantém
      { header: "Empresa", width: 28 },
      {
        header: "Data Venc.",
        width: 12,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
          numFmt: "DD/MM/YYYY",
        },
      },
      {
        header: "Data Liq./Rec.",
        width: 14,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
          numFmt: "DD/MM/YYYY",
        },
      },
      {
        header: "Natureza",
        width: 12,
        style: { alignment: { horizontal: "center" } },
      },
      {
        header: "Origem",
        width: 14,
        style: { alignment: { horizontal: "center" } },
      },
      { header: "Favorecido / Sacado", width: 40 },
      { header: "Conta", width: 22 },
      { header: "Banco", width: 22 },
      { header: "Agencia", width: 22 },
      { header: "Corrente", width: 22 },
      { header: "Forma Pagto.", width: 16 },
      {
        header: "Descrição",
        width: 60,
        style: { alignment: { horizontal: "left" } },
      },
      {
        header: "Valor",
        width: 14,
        style: { alignment: { horizontal: "right" }, numFmt: "#,##0.00" },
      },
    ];

    const rows = movs.map((r) => [
      r.id || "", // << agora “Documento”
      r.empresa || "",
      moment(r.data_vencimento, "DD/MM/YYYY", true).isValid()
        ? moment(r.data_vencimento, "DD/MM/YYYY").format("DD/MM/YYYY")
        : r.data_vencimento || "",
      r.data_liquidacao &&
      moment(r.data_liquidacao, "DD/MM/YYYY", true).isValid()
        ? moment(r.data_liquidacao, "DD/MM/YYYY").format("DD/MM/YYYY")
        : r.data_liquidacao || "",
      natLabel(r.natureza),
      orgLabel(r.tipo),
      (r.fornecedor || "").toString().toUpperCase().trim(),
      r.banco || "",
      r.banco_nome || "",
      r.banco_agencia || "",
      r.banco_conta || "",
      r.forma || "",
      clean(r.historico),
      Number(r.valor) || 0,
    ]);

    const totalGeral = movs.reduce((acc, r) => acc + (Number(r.valor) || 0), 0);
    if (movs.length)
      rows.push(["TOTAL", "", "", "", "", "", "", "", "", "", totalGeral]);

    return {
      headerTitle: "Fluxo Financeiro (Despesas / Receitas)",
      sheetName: "Fluxo Financeiro",
      columns,
      rows,
      pageSetup: {
        orientation: "landscape",
        fitToPage: true,
        fitToHeight: 100,
        paperSize: 9,
        margins: {
          left: 0.7,
          right: 0.7,
          top: 0.75,
          bottom: 0.75,
          header: 0.3,
          footer: 0.3,
        },
      },
    };
  };

  const LicencasComponent: IFormFieldReport = {
    fieldKey: "id_tokens",
    type: "custom",
    colSize: 12,
    render: (methods) => {
      const { control, setValue } = methods;

      // 1) memo: evita nova referência a cada render
      const formattedOptions = React.useMemo(
        () => getSelectOptions(tokens, "id_token", `{empresa} [{licenca}]`),
        [tokens]
      );

      // 3) guarda para não reinicializar após interação do usuário
      const initializedRef = React.useRef(false);

      React.useEffect(() => {
        if (!initializedRef.current && formattedOptions.length > 0) {
          const allValues = formattedOptions.map((o) => o.value);
          setValue("id_tokens", allValues, {
            shouldValidate: true,
            shouldDirty: true,
          });
          initializedRef.current = true;
        }
      }, [formattedOptions, setValue]);

      return (
        <Row>
          <Col>
            <Form.Label>Empresas:</Form.Label>
            <Controller
              name="id_tokens"
              control={control}
              defaultValue={[]}
              render={({ field: { onChange, value } }) => {
                // 2) usa formattedOptions diretamente (sem state local)
                const selectedTokens = formattedOptions.filter((opt) =>
                  (value || []).includes(opt.value)
                );

                const handleChangeConds = (selected: any) => {
                  const newVals = selected
                    ? selected.map((s: any) => s.value)
                    : [];
                  onChange(newVals);
                };

                return (
                  <SelectComp
                    isMulti
                    options={formattedOptions}
                    onChange={handleChangeConds}
                    value={selectedTokens}
                    noOptionsMessage={() => "Sem resultados"}
                    placeholder={"Selecione as licenças..."}
                  />
                );
              }}
            />
          </Col>
        </Row>
      );
    },
  };

  // --------- fields (sem Formas/Remessas/Grupos) ----------
  const fields: IFormFieldReport[] = [
    {
      fieldKey: "dt_inicio",
      label: "Data Inicial (Vencto.)",
      type: "date",
      validation: { required: "Data inicial é obrigatória" },
      colSize: 4,
    },
    {
      fieldKey: "dt_fim",
      label: "Data Final (Vencto.)",
      type: "date",
      validation: { required: "Data final é obrigatória" },
      colSize: 4,
    },
    {
      fieldKey: "status",
      label: "Status das Despesas",
      type: "select",
      placeholder: "Selecione...",
      options: STATUS_DESPESAS_ASSINATURAS,
      colSize: 4,
    },
    LicencasComponent,
  ];

  // --------- request do relatório ----------
  const onRequestReport = async (data: any) => {
    try {
      setLoading(true);

      const query =
        `?dt_inicio=${moment(data.dt_inicio, "YYYY-MM-DD").format("MM/DD/YYYY")}&` +
        `dt_fim=${moment(data.dt_fim, "YYYY-MM-DD").format("MM/DD/YYYY")}&` +
        `status=${data.status}`;

      const tokensBody = tokens.filter((t) =>
        data.id_tokens?.includes(t.id_token || "")
      );

      const result = await new ApiReportFinancial({
        tokens: tokensBody,
      }).movimentacao(query);

      console.log("result", result);

      if (result.success) {
        setResults(result.data);
      }

      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  const columnsTable: ITableDataColumns[] = [
    {
      columnKey: "id",
      label: "ID",
      alignText: "text-center",
    },
    {
      columnKey: "empresa",
      label: "Empresa",
      alignText: "text-center",
    },
    {
      columnKey: "data_vencimento",
      label: "Data Vento.",
      alignText: "text-center",
    },
    {
      columnKey: "data_lancamento",
      label: "Data Lancto.",
      alignText: "text-center",
    },
    {
      columnKey: "data_liquidacao",
      label: "Data Liq.",
      alignText: "text-center",
    },
    // {
    //   columnKey: "natureza",
    //   label: "Natureza",
    //   alignText: "text-center",
    // },
    {
      columnKey: "origem",
      label: "Origem",
      alignText: "text-center",
      render: (item: IMovimentacao) => {
        if (item.tipo === "eventuais") return "Receita Extra";
        if (item.tipo === "despesas") return "Despesa";
        if (item.tipo === "receitas") return "Receita";
        return item.tipo;
      },
    },
    {
      columnKey: "fornecedor",
      label: "Favorecido / Sacado",
      alignText: "text-center",
      render(item: IMovimentacao) {
        return item.fornecedor.toUpperCase().trim();
      },
    },
    // {
    //   columnKey: "banco",
    //   label: "Conta",
    //   alignText: "text-center",
    // },
    {
      columnKey: "contas",
      label: "Dados Banco",
      alignText: "text-center",
      render: (item: IMovimentacao) => {
        if (item.banco_nome !== "") {
          return (
            <div>
              <div>{new StringsFuncs(item.banco).cammelCase()}</div>
              <div>
                {item.banco_nome} [{item.banco_codigo}]
              </div>
              <div>AG {item.banco_agencia}</div>
              <div>CC {item.banco_conta}</div>
            </div>
          );
        } else {
          return "-";
        }
      },
    },
    {
      columnKey: "forma",
      label: "Forma de Pagto.",
      alignText: "text-center",
    },
    {
      columnKey: "planos",
      label: "Contábil",
      alignText: "text-start",
      render: (item: IMovimentacao) => {
        var items: JSX.Element[] = [];

        if (item.planos) {
          items = item.planos.map((i) => {
            return (
              <div>
                {i.conta} - {i.descricao} [
                {numeral(parseFloat(i.percentual.toString()) / 100).format(
                  "0.00%"
                )}
                ]
              </div>
            );
          });
        }

        return <div>{items}</div>;
      },
    },
    {
      columnKey: "historico",
      label: "Descrição",
      alignText: "text-center",
      render: (item: IMovimentacao) => stripHtmlHistorico(item.historico || ""),
      sortValue: (item: IMovimentacao) => (item.historico || "").toLowerCase(),
    },
    {
      columnKey: "valor",
      label: "Bruto",
      alignText: "text-end",
      render: (item: IMovimentacao) => {
        const valor = numeral(item.valor?.toString().replace(".", ",")).format(
          "#,##0.00"
        );
        return (
          <span
            style={{ color: `${item.natureza == "credito" ? "blue" : "red"}` }}
          >
            {valor}
          </span>
        );
      },
      sortValue: (item: IMovimentacao) => {
        return item.valor ? parseFloat(item.valor.toString()) : 0;
      },
    },
  ];

  const totalizer = () => {
    if (!results || !Array.isArray(results)) return <></>;

    const movs = results as IMovimentacao[];

    const toNum = (v: unknown) => {
      const n = parseFloat(String(v ?? 0).replace(",", "."));
      return Number.isFinite(n) ? n : 0;
    };

    // Helpers de filtro
    const isDespesa = (m: IMovimentacao) =>
      m.tipo === "despesas" || m.natureza === "debito";
    const isReceita = (m: IMovimentacao) => m.tipo === "receitas";
    const isReceitaEventual = (m: IMovimentacao) => m.tipo === "eventuais";

    // Totais gerais
    const sum = (arr: IMovimentacao[]) =>
      arr.reduce((acc, i) => acc + toNum(i.valor), 0);

    const totalDespesas = sum(movs.filter(isDespesa)); // (negativo)
    const totalReceitas = sum(movs.filter(isReceita)); // (positivo)
    const totalReceitasEventuais = sum(movs.filter(isReceitaEventual)); // (positivo)
    const totalLiquido = sum(movs); // receitas - despesas (com sinal)

    // Agrupar por empresa com colunas de cada categoria
    type Agg = {
      empresa: string;
      dCount: number;
      dTotal: number;
      rCount: number;
      rTotal: number;
      reCount: number;
      reTotal: number;
      count: number;
      total: number; // total líquido
    };

    const porEmpresaMap = new Map<string, Agg>();

    movs.forEach((m) => {
      const emp = m.empresa || "(sem empresa)";
      const val = toNum(m.valor);

      let agg = porEmpresaMap.get(emp);
      if (!agg) {
        agg = {
          empresa: emp,
          dCount: 0,
          dTotal: 0,
          rCount: 0,
          rTotal: 0,
          reCount: 0,
          reTotal: 0,
          count: 0,
          total: 0,
        };
        porEmpresaMap.set(emp, agg);
      }

      if (isDespesa(m)) {
        agg.dCount += 1;
        agg.dTotal += val;
      } else if (isReceita(m)) {
        agg.rCount += 1;
        agg.rTotal += val;
      } else if (isReceitaEventual(m)) {
        agg.reCount += 1;
        agg.reTotal += val;
      }

      agg.count += 1;
      agg.total += val; // líquido
    });

    const porEmpresa = Array.from(porEmpresaMap.values()).sort(
      (a, b) => Math.abs(b.total) - Math.abs(a.total)
    ); // ordena pelo impacto

    // Totais de rodapé por coluna (somatório das empresas)
    const foot = porEmpresa.reduce(
      (acc, e) => ({
        dCount: acc.dCount + e.dCount,
        dTotal: acc.dTotal + e.dTotal,
        rCount: acc.rCount + e.rCount,
        rTotal: acc.rTotal + e.rTotal,
        reCount: acc.reCount + e.reCount,
        reTotal: acc.reTotal + e.reTotal,
        count: acc.count + e.count,
        total: acc.total + e.total,
      }),
      {
        dCount: 0,
        dTotal: 0,
        rCount: 0,
        rTotal: 0,
        reCount: 0,
        reTotal: 0,
        count: 0,
        total: 0,
      }
    );

    const money = (n: number) => numeral(n).format("$ #,##0.00");

    return (
      <div style={{ fontSize: "14px" }}>
        {/* Totais gerais por categoria */}
        <Row className="my-2">
          <Col>
            <strong>Totalizador — Geral</strong>
          </Col>
        </Row>

        <Row className="py-1 border-bottom">
          <Col>Despesas</Col>
          <Col style={{ textAlign: "center" }}>
            {movs.filter(isDespesa).length}
          </Col>
          <Col style={{ textAlign: "center" }}>{money(totalDespesas)}</Col>
        </Row>

        <Row className="py-1 border-bottom">
          <Col>Receitas</Col>
          <Col style={{ textAlign: "center" }}>
            {movs.filter(isReceita).length}
          </Col>
          <Col style={{ textAlign: "center" }}>{money(totalReceitas)}</Col>
        </Row>

        <Row className="py-1 border-bottom">
          <Col>Receitas eventuais</Col>
          <Col style={{ textAlign: "center" }}>
            {movs.filter(isReceitaEventual).length}
          </Col>
          <Col style={{ textAlign: "center" }}>
            {money(totalReceitasEventuais)}
          </Col>
        </Row>

        <Row className="py-1 fw-bold">
          <Col>Saldo líquido</Col>
          <Col style={{ textAlign: "center" }}>{movs.length}</Col>
          <Col style={{ textAlign: "center" }}>{money(totalLiquido)}</Col>
        </Row>

        {/* Quebra por empresa com colunas por categoria */}
        <Row className="my-3">
          <Col>
            <strong>Totalizador — Por empresa</strong>
          </Col>
        </Row>

        <Row className="fw-bold border-bottom pb-1">
          <Col>Empresa</Col>
          <Col style={{ textAlign: "center" }}>Qtd D</Col>
          <Col style={{ textAlign: "center" }}>Total D</Col>
          <Col style={{ textAlign: "center" }}>Qtd R</Col>
          <Col style={{ textAlign: "center" }}>Total R</Col>
          <Col style={{ textAlign: "center" }}>Qtd RE</Col>
          <Col style={{ textAlign: "center" }}>Total RE</Col>
          <Col style={{ textAlign: "center" }}>Qtd</Col>
          <Col style={{ textAlign: "center" }}>Líquido</Col>
        </Row>

        {porEmpresa.map((e) => (
          <Row key={e.empresa} className="py-1 border-bottom">
            <Col>{e.empresa}</Col>
            <Col style={{ textAlign: "center" }}>{e.dCount}</Col>
            <Col style={{ textAlign: "center" }}>{money(e.dTotal)}</Col>
            <Col style={{ textAlign: "center" }}>{e.rCount}</Col>
            <Col style={{ textAlign: "center" }}>{money(e.rTotal)}</Col>
            <Col style={{ textAlign: "center" }}>{e.reCount}</Col>
            <Col style={{ textAlign: "center" }}>{money(e.reTotal)}</Col>
            <Col style={{ textAlign: "center" }}>{e.count}</Col>
            <Col style={{ textAlign: "center" }}>{money(e.total)}</Col>
          </Row>
        ))}

        {/* Rodapé de somatórios */}
        <Row className="py-2 fw-bold">
          <Col>Total</Col>
          <Col style={{ textAlign: "center" }}>{foot.dCount}</Col>
          <Col style={{ textAlign: "center" }}>{money(foot.dTotal)}</Col>
          <Col style={{ textAlign: "center" }}>{foot.rCount}</Col>
          <Col style={{ textAlign: "center" }}>{money(foot.rTotal)}</Col>
          <Col style={{ textAlign: "center" }}>{foot.reCount}</Col>
          <Col style={{ textAlign: "center" }}>{money(foot.reTotal)}</Col>
          <Col style={{ textAlign: "center" }}>{foot.count}</Col>
          <Col style={{ textAlign: "center" }}>{money(foot.total)}</Col>
        </Row>
      </div>
    );
  };

  return (
    <DefaultBasePage
      title="Relatórios - Movimentação Financeira (Empresas)"
      subtitle={`Resultados: `}
      h3
      loading={loading}
      message={messages}
    >
      <DefaultReportPage
        titleFilters="Filtros de Movimentação Financeira (Empresas)"
        titleResults="Resultados"
        fields={fields}
        onRequestReport={onRequestReport}
        resultsData={results}
        columnsTable={columnsTable}
        defaultValues={{
          dt_inicio: prevMonthStart,
          dt_fim: prevMonthEnd,
          status: "0",
          id_tokens: [],
        }}
        totalizerPosition="mirror"
        totalizerElement={totalizer()}
        setLoading={setLoading}
        setMessagesExport={setMessages}
        getExportDataPDF={getExportDataPDF}
        getExportDataExcel={getExportDataExcel}
        exportButtonTitle="Exportar"
        reportName="Movimentação Financeira (Empresas)"
      />
    </DefaultBasePage>
  );
};

export default RelatoriosMovimentacaoEmpresasPrivatePage;
